// src/modules/units/tyres/views/retread-batch/retreadbatchprint.js
//
// "Print" and "Email" actions for the retread-batch detail. Both gather what the
// consignment sheet needs — the batch, its supplier name, the customer record (name,
// delivery-or-postal address, contact details), and the full tyre record behind
// each line — then either render the pure document (retreadbatchprint.view.js) into
// an isolated, off-screen <iframe> and print it, or open the user's mail client
// with the sheet (see emailRetreadBatch).
//
// The gathering is the whole job. Today GET /retread-batch/{id} lines carry only
// externalTyreId + retread-lifecycle fields, so serial / RFID / casing are NOT on
// the batch and each tyre is fetched by id (GET /tyre/{id}). That is the part worth
// asking the backend to change: enriching the lines with the tyre identity fields
// (or a bulk id lookup) removes N round-trips on a big consignment. This is written
// for that day — a line that ALREADY carries the identity fields is used as-is and
// its per-tyre GET is skipped, so no client change is needed when the API improves.
// The customer lookup (GET /customer/{id}) and every tyre fetch are best-effort:
// a tyre that fails to load still prints its id, an unresolved customer prints a
// dash — the sheet is never blocked by one lookup.

import { fetchTyre } from '../tyres.api.js';
import { fetchCustomer, fetchCustomers } from '../../../../users/views/customers.api.js';
import { getSelectedYard } from '../../shared/tyre-scope.js';
import { RETREAD_LINE_STATUS, RETREAD_BATCH_STATUS } from '../../shared/retread-batch-core.js';
import { printDocumentHTML, emailSubject, emailText, emailHTML } from './retreadbatchprint.view.js';

const isBlank = (v) => v === null || v === undefined || v === '';
const lineTyreId = (l) => l?.externalTyreId ?? l?.tyreId ?? '';

// Line statuses that mean a tyre has come back — used to decide which sheet to print.
const RETURNED_STATUSES = [
  RETREAD_LINE_STATUS.RETURNED_RETREADED,
  RETREAD_LINE_STATUS.RETURNED_REJECTED,
  RETREAD_LINE_STATUS.WRITTEN_OFF
];

// The single Print button adapts to the batch's status: once any tyre has come back
// (or the batch is part-returned / closed) it prints the received record; otherwise
// the dispatch consignment sheet.
function pickMode(batch, lines) {
  const anyReturned = (Array.isArray(lines) ? lines : [])
    .some((l) => RETURNED_STATUSES.includes(l?.status ?? l?.lineStatus));
  const status = batch?.status;
  if (anyReturned || status === RETREAD_BATCH_STATUS.PARTIALLY_RETURNED || status === RETREAD_BATCH_STATUS.CLOSED) {
    return 'received';
  }
  return 'dispatch';
}

// The header yard scope's customer, when one is selected — a good fallback for the
// batch's own customer since the tyre area is almost always scoped to it.
function selectedYardCustomer() {
  const yard = getSelectedYard();
  return yard?.customerName || '';
}

// Resolve batch.customerId -> { customer, customerName }. The full record comes from
// GET /customer/{id} (name, delivery/postal address, contact details). If that
// fails, the name alone is looked up in GET /customer (the customers the signed-in
// user can see), then the header yard scope's customerName, then '' (the view shows
// a dash) — a lookup miss never blocks the sheet.
async function resolveCustomer(customerId) {
  if (isBlank(customerId)) return { customer: null, customerName: selectedYardCustomer() };
  const customer = await fetchCustomer(customerId);
  if (customer && (customer.name || customer.customerName)) {
    return { customer, customerName: customer.name || customer.customerName };
  }
  try {
    const customers = await fetchCustomers();
    if (Array.isArray(customers)) {
      const match = customers.find((c) => String(c?.customerId) === String(customerId));
      const name = match?.customerName || match?.name;
      if (name) return { customer: customer || match, customerName: name };
    }
  } catch (_) { /* fall through to the scope fallback */ }
  return { customer, customerName: selectedYardCustomer() };
}

// True once the batch endpoint enriches a line with the tyre identity fields the
// sheet prints. When it does, the line IS the record and no per-tyre GET is needed;
// until then this is false for every line and each is fetched.
function lineHasTyreDetail(l) {
  return !!(l && (
    l.tyreSerialNumber != null || l.tyreRFID != null ||
    l.tyreBrand != null || l.tyreMake != null || l.tyreModel != null ||
    l.tyreSize != null || l.tyreType != null
  ));
}

// Resolve each line to { externalTyreId, tyre|null, line } in line order. The `line`
// (the batch line) is always carried through — the received sheet reads its outcome
// and cost from it. A line that already carries the tyre identity fields is used as
// the record directly; otherwise the full record is fetched (GET /tyre/{id}), at most
// `limit` in flight at once so a large consignment doesn't open one request per tyre
// simultaneously. A failed or empty fetch yields tyre:null and the row still shows
// the id. fetchTyre resolves the record on res.data (see tyredetail.js).
async function gatherLineTyres(lines, { limit = 6 } = {}) {
  const src = Array.isArray(lines) ? lines : [];
  const out = new Array(src.length);
  let cursor = 0;

  async function worker() {
    for (;;) {
      const i = cursor++;
      if (i >= src.length) return;
      const line = src[i];
      const raw = lineTyreId(line);
      const externalTyreId = raw == null ? '' : String(raw);

      if (lineHasTyreDetail(line)) { out[i] = { externalTyreId, tyre: line, line }; continue; }

      let tyre = null;
      if (externalTyreId) {
        try {
          const res = await fetchTyre(externalTyreId);
          if (res && res.success && res.data) tyre = res.data;
        } catch (_) { tyre = null; }
      }
      out[i] = { externalTyreId, tyre, line };
    }
  }

  const count = src.length ? Math.min(limit, src.length) : 0;
  await Promise.all(Array.from({ length: count }, worker));
  return out;
}

// Render an HTML *document* string into a hidden iframe and print it. `srcdoc` keeps
// it fully isolated from the app; cleanup runs on afterprint with a timeout
// backstop. Resolves once printing has been triggered (not when the dialog closes).
function printDocument(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

    let cleaned = false;
    function cleanup() {
      if (cleaned) return;
      cleaned = true;
      // Remove a touch later so the print dialog isn't torn out from under itself.
      setTimeout(() => { try { iframe.remove(); } catch (_) { /* already gone */ } }, 800);
    }

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) { cleanup(); resolve(); return; }
      try { win.addEventListener('afterprint', cleanup); } catch (_) { /* ignore */ }
      try { win.focus(); win.print(); } catch (_) { /* ignore */ }
      setTimeout(cleanup, 60000); // backstop if afterprint never fires (some engines)
      resolve();
    };

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

// Everything the sheet needs, gathered once: customer and tyre records in parallel
// — neither blocks the other.
async function gatherSheet(batch, supplierName) {
  const lines = Array.isArray(batch.lines) ? batch.lines : [];
  const [{ customer, customerName }, tyres] = await Promise.all([
    resolveCustomer(batch.customerId),
    gatherLineTyres(lines)
  ]);
  return { batch, supplierName, customer, customerName, tyres, mode: pickMode(batch, lines) };
}

/**
 * Gather + print one retread batch. Best-effort throughout: only truly fails if the
 * document couldn't be produced at all.
 *
 * @param {object} batch  the current (already-fetched) batch, with .lines
 * @param {object} [opts]
 * @param {string} [opts.supplierName]  supplier name the detail already resolved
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function printRetreadBatch(batch, { supplierName = '' } = {}) {
  if (!batch || !batch.retreadBatchId) {
    return { success: false, message: 'No batch to print.' };
  }

  const sheet = await gatherSheet(batch, supplierName);

  try {
    const html = printDocumentHTML({ ...sheet, printedAt: new Date() });
    await printDocument(html);
    return { success: true };
  } catch (_) {
    return { success: false, message: 'Could not prepare the print document.' };
  }
}

// Longest mailto: URL handed to the OS. Mail clients (Outlook especially) truncate
// or refuse much beyond ~2000 characters, so a long tyre list travels via the
// clipboard instead of the body.
const MAX_MAILTO_LENGTH = 1900;

// Put the sheet on the clipboard as rich HTML (+ plain text). Called SYNCHRONOUSLY
// from the click so the browser still counts it as a user gesture; the content is
// supplied as promises that resolve once the gather finishes. Resolves true/false.
function copySheetToClipboard(htmlPromise, textPromise) {
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      return Promise.resolve(false);
    }
    const item = new ClipboardItem({
      'text/html': htmlPromise.then((h) => new Blob([h], { type: 'text/html' })),
      'text/plain': textPromise.then((t) => new Blob([t], { type: 'text/plain' }))
    });
    return navigator.clipboard.write([item]).then(() => true, () => false);
  } catch (_) {
    return Promise.resolve(false);
  }
}

function openMailto(url) {
  const a = document.createElement('a');
  a.href = url;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Email one retread batch. There is no mail-sending endpoint, so this opens the
 * user's own mail client (mailto:) addressed to the natural recipient — the
 * supplier for a dispatch consignment, the customer's contact for a received
 * record — with the subject and a plain-text sheet as the body. A formatted (HTML)
 * copy of the sheet goes on the clipboard to paste in. When the full body is too
 * long for a mailto: link, the body carries the header only and points at the
 * clipboard copy.
 *
 * Call it directly from the click handler (no await before it) so the clipboard
 * write keeps the user gesture.
 *
 * @param {object} batch  the current (already-fetched) batch, with .lines
 * @param {object} [opts]
 * @param {string} [opts.supplierName]
 * @param {string} [opts.supplierEmail]  the supplier's eMailAddress, when known
 * @returns {Promise<{ success: boolean, copied?: boolean, truncated?: boolean, to?: string, message?: string }>}
 */
export async function emailRetreadBatch(batch, { supplierName = '', supplierEmail = '' } = {}) {
  if (!batch || !batch.retreadBatchId) {
    return { success: false, message: 'No batch to email.' };
  }

  const sheetPromise = gatherSheet(batch, supplierName);
  const copied = copySheetToClipboard(
    sheetPromise.then((sheet) => emailHTML(sheet)),
    sheetPromise.then((sheet) => emailText(sheet))
  );

  try {
    const sheet = await sheetPromise;
    const to = sheet.mode === 'received'
      ? String(sheet.customer?.contactEmailAddress || '').trim()
      : String(supplierEmail || '').trim();
    const base = `mailto:${encodeURIComponent(to).replace(/%40/g, '@')}` +
      `?subject=${encodeURIComponent(emailSubject(sheet))}&body=`;

    let url = base + encodeURIComponent(emailText(sheet));
    let truncated = false;
    if (url.length > MAX_MAILTO_LENGTH) {
      url = base + encodeURIComponent(emailText(sheet, { includeTyres: false }));
      truncated = true;
    }

    const didCopy = await copied;
    openMailto(url);
    return { success: true, copied: didCopy, truncated, to };
  } catch (_) {
    return { success: false, message: 'Could not prepare the email.' };
  }
}
